import { useMemo } from 'react'

import { usePortal } from '../../../../../app/_providers/PortalProvider.client'
import { UseEmbroideryProps } from '../../../../../containers/EmbroideryService'
import { Embroidery, EmbroideryVariant, Variant } from '../../../../../types/graphql'
import { isTextEmbroideryColorKey } from '../../../../embroidery/forms/selectors/ColorSelector'

/** Derive portal-specific embroidery settings that are provided to `EmbroideryService`. */
export function usePortalEmbroiderySettings({
  isPortalColorSelected,
  embroideryOptions = [],
  mandatoryLogoEmbroidery = false,
}: {
  isPortalColorSelected: boolean
  embroideryOptions?: readonly Embroidery<EmbroideryVariant | Variant>[]
  mandatoryLogoEmbroidery: boolean
}) {
  const { portal } = usePortal()

  // Define the hidden fields for the embroidery modal forms
  const hiddenEmbroideryFields = useMemo<UseEmbroideryProps['hideFields']>(() => {
    if (!isPortalColorSelected) return {}

    return {
      TEXT_ONLY: portal?.embroidery?.hiddenTextEmbroideryFields ?? [],
    }
  }, [portal?.embroidery?.hiddenTextEmbroideryFields, isPortalColorSelected])

  // Define a default set of form values that should be applied to the forms within the embroidery modal.
  // This is derived from the `defaultTextEmbroideryValues` field on the portal's embroidery settings object.
  const defaultInitialFormValues = useMemo<UseEmbroideryProps['defaultInitialFormValues']>(() => {
    const out: UseEmbroideryProps['defaultInitialFormValues'] = {}

    // Return early if there is no embroidery settings defined by the portal, or if no portal color is currently selected
    if (!portal?.embroidery || !isPortalColorSelected) return out

    const defaultPortalTextEmbroideryValues = portal?.embroidery?.defaultTextEmbroideryValues ?? {}
    const hasDefaultPortalTextEmbroideryValues =
      Object.values(defaultPortalTextEmbroideryValues).filter(Boolean).length > 0

    // If a portal doesn't have default text embroidery values defined, return early
    if (hasDefaultPortalTextEmbroideryValues === false) return out

    const textOnlyOption = embroideryOptions.find(({ kind }) => kind === 'TEXT_ONLY')

    // Only assign default text embroidery values if the TEXT_ONLY option is available
    if (textOnlyOption) {
      out['TEXT_ONLY'] = {}

      // Assign default text placement value if defined
      if (defaultPortalTextEmbroideryValues.textPlacement) {
        const defaultTextPlacement =
          textOnlyOption.textPlacements?.find(
            ({ offsetKey }) => offsetKey === defaultPortalTextEmbroideryValues.textPlacement
          ) ?? textOnlyOption.textPlacements?.[0]

        out['TEXT_ONLY'].textPlacement = defaultTextPlacement
      }
      const defaultPortalTextEmbroideryValuesTextColor = isTextEmbroideryColorKey(
        defaultPortalTextEmbroideryValues.textColor
      )
        ? defaultPortalTextEmbroideryValues.textColor
        : undefined
      // Assign default text color value if defined
      if (defaultPortalTextEmbroideryValues.textColor) {
        out['TEXT_ONLY'].textColor = defaultPortalTextEmbroideryValuesTextColor
      }

      // Assign default font style if defined
      if (defaultPortalTextEmbroideryValues.font) {
        const defaultFontStyle =
          textOnlyOption.textFonts?.find(
            ({ name }) => name === defaultPortalTextEmbroideryValues.font
          ) ?? textOnlyOption.textFonts?.[0]
        out['TEXT_ONLY'].font = defaultFontStyle
      }
    }

    const logoOnlyOption = embroideryOptions.find(({ kind }) => kind === 'LOGO_ONLY')
    if (logoOnlyOption && portal?.embroidery) {
      out['LOGO_ONLY'] = {
        logoName: portal.embroidery.logoName,
        logoImageSource: portal.embroidery.image,
      }
    }

    return out
  }, [portal?.embroidery, embroideryOptions, isPortalColorSelected])

  // This is passed to `EmbroideryService` in order to provide it with embroidery options that should
  // be automatically set. This is used to automatically select logo embroidery for portals that
  // have mandatory text embroidery defined for the current product group category.
  const defaultSelectedEmbroideryOptions = useMemo<
    UseEmbroideryProps['defaultSelectedEmbroideryOptions']
  >(() => {
    const logoOnlyOption = embroideryOptions.find(({ kind }) => kind === 'LOGO_ONLY')
    // Only apply if a portal color is selected & LOGO_ONLY embroidery option is present.
    if (!logoOnlyOption || !isPortalColorSelected || !mandatoryLogoEmbroidery) return {}

    return {
      LOGO_ONLY: {
        logoName: portal?.embroidery?.logoName,
        logoImageSource: portal?.embroidery?.image,
        embroideryItemVariant: logoOnlyOption.variants[0],
      },
    }
  }, [embroideryOptions, portal, isPortalColorSelected, mandatoryLogoEmbroidery])

  // Derive the active second line text embroidery options for the current portal/product selection
  const textEmbroiderySecondLineOptions = useMemo(() => {
    if (!isPortalColorSelected) return []
    return portal?.embroidery?.textEmbroiderySecondLineOptions ?? []
  }, [isPortalColorSelected, portal?.embroidery?.textEmbroiderySecondLineOptions])

  return {
    hiddenEmbroideryFields,
    textEmbroiderySecondLineOptions,
    defaultInitialFormValues,
    defaultSelectedEmbroideryOptions,
  }
}
